// src/pages/BootstrapPage.js
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

function BootstrapPage() {
  const [containerDemo, setContainerDemo] = useState({ className: '', text: 'Demo Content' });

  const toggleContainer = () => {
    setContainerDemo((prev) =>
      prev.className === 'container'
        ? { className: '', text: 'Demo Content' }
        : { className: 'container', text: 'This is now a Bootstrap container!' }
    );
  };

  return (
    <div>
      {/* Hero Section */}
      <header className="hero-section text-center" role="banner">
        <div className="container">
          <h1>Bootstrap Shortcut Assister</h1>
          <p className="lead">Set up and use Bootstrap like a pro</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-5" role="main">
        <section id="bootstrap-content" aria-labelledby="bootstrap-heading">
          <h2 id="bootstrap-heading">Bootstrap for Beginners</h2>
          <p>Bootstrap is a CSS framework that makes building responsive websites easy. Follow these steps to set it up and use it—no teacher needed!</p>

          {/* Chunk 1: Getting Started */}
          <div className="chunk">
            <h3>1. Getting Started with Bootstrap</h3>
            <p>Add Bootstrap to your HTML using a CDN (Content Delivery Network).</p>
            <div className="code-snippet">
              <pre>
                {`<!-- In <head> -->
<link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css" rel="stylesheet">

<!-- Before </body> -->
<script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/js/bootstrap.bundle.min.js"></script>`}
              </pre>
            </div>
            <p><strong>Steps:</strong></p>
            <ol>
              <li>Create a new HTML file (e.g., <code>index.html</code>).</li>
              <li>Copy the CSS link into the <code>&lt;head&gt;</code>.</li>
              <li>Copy the JS script before the closing <code>&lt;/body&gt;</code>.</li>
            </ol>
            <div className="tip">
              <strong>VS Code Tip:</strong> Type <code>link</code> or <code>script</code> and press <strong>Tab</strong> to auto-generate these tags.
            </div>
          </div>

          {/* Chunk 2: Basic Template */}
          <div className="chunk">
            <h3>2. Basic Bootstrap Template</h3>
            <p>Start with this simple template to ensure Bootstrap works.</p>
            <div className="code-snippet">
              <pre>
                {`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>My Bootstrap Site</title>
  <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css" rel="stylesheet">
</head>
<body>
  <h1>Hello, Bootstrap!</h1>
  <script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/js/bootstrap.bundle.min.js"></script>
</body>
</html>`}
              </pre>
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Type <code>!</code> and press <strong>Tab</strong> to generate a basic HTML structure, then add Bootstrap links.
            </div>
          </div>

          {/* Chunk 3: Containers */}
          <div className="chunk">
            <h3>3. Containers</h3>
            <p>Use <code>.container</code> or <code>.container-fluid</code> to center and pad your content.</p>
            <div className="code-snippet">
              <pre>
                {`<div class="container">
  <p>This is centered with padding.</p>
</div>
<div class="container-fluid">
  <p>This spans the full width.</p>
</div>`}
              </pre>
            </div>
            <p><strong>Try it:</strong> Click to toggle a container!</p>
            <button className="btn btn-pill" onClick={toggleContainer}>Toggle Container</button>
            <div id="demo-area" className={containerDemo.className}>{containerDemo.text}</div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Press <strong>Cmd + D</strong> (Mac) or <strong>Ctrl + D</strong> (Windows) to select and edit multiple instances of "container" at once.
            </div>
          </div>

          {/* Chunk 4: Grid System */}
          <div className="chunk">
            <h3>4. Grid System</h3>
            <p>Bootstrap’s 12-column grid uses <code>.row</code> and <code>.col</code> classes.</p>
            <div className="code-snippet">
              <pre>
                {`<div class="row">
  <div class="col-6">Half width</div>
  <div class="col-6">Half width</div>
</div>`}
              </pre>
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Use <strong>Cmd + Shift + L</strong> (Mac) or <strong>Ctrl + Shift + L</strong> (Windows) to select all occurrences of "col" for quick edits.
            </div>
          </div>

          {/* Chunk 5: Buttons */}
          <div className="chunk">
            <h3>5. Buttons</h3>
            <p>Bootstrap offers various button styles using <code>.btn</code> with different classes for colors and purposes.</p>
            <div className="code-snippet">
              <pre>
                {`<button class="btn btn-primary">Primary</button>
<button class="btn btn-secondary">Secondary</button>
<button class="btn btn-success">Success</button>
<button class="btn btn-danger">Danger</button>
<button class="btn btn-warning">Warning</button>
<button class="btn btn-info">Info</button>
<button class="btn btn-light">Light</button>
<button class="btn btn-dark">Dark</button>
<button class="btn btn-link">Link</button>`}
              </pre>
            </div>
            <p><strong>Try them:</strong> Here are all Bootstrap button types in action!</p>
            <div>
              <button className="btn btn-primary button-demo">Primary</button>
              <button className="btn btn-secondary button-demo">Secondary</button>
              <button className="btn btn-success button-demo">Success</button>
              <button className="btn btn-danger button-demo">Danger</button>
              <button className="btn btn-warning button-demo">Warning</button>
              <button className="btn btn-info button-demo">Info</button>
              <button className="btn btn-light button-demo">Light</button>
              <button className="btn btn-dark button-demo">Dark</button>
              <button className="btn btn-link button-demo">Link</button>
            </div>
            <p><strong>When to Use:</strong></p>
            <ul>
              <li><code>btn-primary</code>: Main actions (e.g., "Submit")</li>
              <li><code>btn-secondary</code>: Secondary actions (e.g., "Cancel")</li>
              <li><code>btn-success</code>: Positive actions (e.g., "Save")</li>
              <li><code>btn-danger</code>: Destructive actions (e.g., "Delete")</li>
              <li><code>btn-warning</code>: Caution actions (e.g., "Proceed with Care")</li>
              <li><code>btn-info</code>: Informational actions (e.g., "Learn More")</li>
              <li><code>btn-light</code>: Minimal style (e.g., light backgrounds)</li>
              <li><code>btn-dark</code>: High contrast (e.g., dark backgrounds)</li>
              <li><code>btn-link</code>: Looks like a link but behaves like a button</li>
            </ul>
            <div className="tip">
              <strong>VS Code Tip:</strong> Press <strong>Cmd + /</strong> (Mac) or <strong>Ctrl + /</strong> (Windows) to comment out a button style you’re testing.
            </div>
          </div>

          {/* Chunk 6: Navbar */}
          <div className="chunk">
            <h3>6. Navbar</h3>
            <p>Create a responsive navigation bar (like this site’s!) with <code>.navbar</code>.</p>
            <div className="code-snippet">
              <pre>
                {`<nav class="navbar navbar-expand-lg bg-light">
  <div class="container">
    <a class="navbar-brand" href="#">My Site</a>
    <div class="collapse navbar-collapse">
      <ul class="navbar-nav">
        <li class="nav-item">
          <a class="nav-link" href="#">Home</a>
        </li>
      </ul>
    </div>
  </div>
</nav>`}
              </pre>
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Use <strong>Option + Up/Down</strong> (Mac) or <strong>Alt + Up/Down</strong> (Windows) to move lines of code (e.g., reorder nav items).
            </div>
          </div>

          {/* Chunk 7: Troubleshooting */}
          <div className="chunk">
            <h3>7. Troubleshooting</h3>
            <p>Common issues and fixes when setting up Bootstrap:</p>
            <ul>
              <li><strong>Not Working?</strong> Check if the CDN links are correct and in the right place.</li>
              <li><strong>JS Features Fail?</strong> Ensure the script is before <code>&lt;/body&gt;</code>, not in <code>&lt;head&gt;</code>.</li>
              <li><strong>Styles Missing?</strong> Clear your browser cache (Cmd + Shift + R on Mac, Ctrl + F5 on Windows).</li>
              <li><strong>Need Help?</strong> Visit <NavLink to="https://getbootstrap.com/docs/5.3/getting-started/introduction/" target="_blank">Bootstrap Docs</NavLink>.</li>
            </ul>
            <div className="tip">
              <strong>VS Code Tip:</strong> Press <strong>Cmd + T</strong> (Mac) or <strong>Ctrl + T</strong> (Windows) to search for Bootstrap classes or files in your project.
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default BootstrapPage;